import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { listTimeZones, timeZoneName } from '../../../shared/dashboard/dashboardFormat';
import { IdentityContext } from '../../identity/state/IdentityContext';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { identityGateway } from '../../identity/services';

/**
 * Shows the company's time zone in the dashboard header and, for someone who may edit the business profile, lets them choose it right here (a dependency is
 * created in place: nothing the user is looking at is lost). Until a zone is chosen the dashboard uses UTC and says so. The server owns the rule: it only accepts
 * a zone it knows, requires `businessprofile.write`, and audits the change.
 */
export function CompanyTimeZoneControl({ effectiveZoneId, isSet, onSaved }: { effectiveZoneId: string; isSet: boolean; onSaved: () => void }) {
  const canEdit = usePermissionCheck()('businessprofile.write');
  const identity = useContext(IdentityContext);
  const [open, setOpen] = useState(false);

  return (
    <>
      <span data-testid="company-time-zone">
        {isSet ? `${timeZoneName(effectiveZoneId)} (${effectiveZoneId})` : 'UTC (not set)'}
      </span>
      {canEdit ? (
        <button type="button" className="ed-exec-header__action" onClick={() => setOpen(true)}>
          {isSet ? 'Change time zone' : 'Set time zone'}
        </button>
      ) : !isSet ? (
        <span className="ed-exec-header__hint"> Ask an administrator to set it.</span>
      ) : null}
      {open && (
        <TimeZoneDialog
          initialZone={isSet ? effectiveZoneId : ''}
          onClose={() => setOpen(false)}
          onSave={async (zone) => {
            await identityGateway.setCompanyTimeZone(zone);
            await identity?.refresh();
            setOpen(false);
            onSaved();
          }}
        />
      )}
    </>
  );
}

function TimeZoneDialog({ initialZone, onClose, onSave }: { initialZone: string; onClose: () => void; onSave: (zone: string) => Promise<void> }) {
  const [zone, setZone] = useState(initialZone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const opener = useRef<Element | null>(document.activeElement);
  const titleId = useId();
  const selectId = useId();
  const zones = useMemo(() => listTimeZones(), []);

  useEffect(() => {
    selectRef.current?.focus();
    const previouslyFocused = opener.current;
    return () => {
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  const save = async () => {
    if (!zone) {
      setError('Choose a time zone.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(zone);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not save the time zone.'));
      setSaving(false);
    }
  };

  return (
    <div className="ed-dialog-backdrop">
      <div
        className="ed-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
      >
        <h3 id={titleId}>Company time zone</h3>
        <p>
          Your reporting months and dates follow this time zone. Choosing it does not change any payroll amount or check date.
        </p>
        <label htmlFor={selectId}>Time zone</label>
        <select id={selectId} ref={selectRef} value={zone} onChange={(e) => setZone(e.target.value)}>
          <option value="">Choose a time zone</option>
          <optgroup label="United States">
            {zones.us.map((z) => <option key={z} value={z}>{`${timeZoneName(z)} (${z})`}</option>)}
          </optgroup>
          {zones.other.length > 0 && (
            <optgroup label="All time zones">
              {zones.other.map((z) => <option key={z} value={z}>{z}</option>)}
            </optgroup>
          )}
        </select>
        {error && <p role="alert" className="ed-dialog__error">{error}</p>}
        <div className="ed-dialog__actions">
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save time zone'}</button>
        </div>
      </div>
    </div>
  );
}
